import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Heading, Button, Text, Chip, BannerNotification } from '@frontend/design-system';
import { useInsuranceReportContainerProviderContext } from '../../context/InsuranceReportContainerProvider';
import { Request as ReportSource } from '../../types/insurance-report-api.types';
import { insuranceReportTrackingIds } from '../../utils/tracking-ids';
import { formatSourceResponse, getInsuranceType, removeUnderScore, reportDateFormat } from '../../utils/utils';
import { ResponseSourceTooltip } from '../common-components/response-source-tooltip';

type HeaderProps = {
  handlePrint?: () => void;
  handleSaveToPms?: () => void;
  closeInsuranceReportModal?: () => void;
  patientFullName: string;
  reportSources: ReportSource[];
  verifiedDate: string;
  insuranceStatus: string;
  insuranceType?: InsuranceType;
  payerCredentialsBanner: {
    showBanner: boolean;
    handleCloseBanner: () => void;
    onManagePayerConnection: () => void;
  };
  isReportLoading: boolean;
};

export const InsuranceReportHeader = React.memo(
  ({
    handlePrint,
    handleSaveToPms,
    closeInsuranceReportModal,
    patientFullName,
    reportSources,
    verifiedDate,
    insuranceStatus,
    insuranceType,
    payerCredentialsBanner,
    isReportLoading,
  }: HeaderProps) => {
    const { t } = useTranslation('insuranceEligibilityReport');
    const { showBanner, handleCloseBanner, onManagePayerConnection } = payerCredentialsBanner;

    const insuranceTypeFormatted = getInsuranceType(insuranceType || InsuranceType.INSURANCE_TYPE_PRIMARY);
    const isActiveInsurance = insuranceStatus?.toLowerCase() === 'active';
    const isInActiveInsurance = insuranceStatus?.toLowerCase() === 'inactive';
    const shouldShowInsuranceStatus = !!insuranceStatus && (isActiveInsurance || isInActiveInsurance);

    const { PMSName, isDocumentCenterWritebackSupported, isLoading } = useInsuranceReportContainerProviderContext();

    const shouldShowSaveToPmsButton = isDocumentCenterWritebackSupported && PMSName && !isLoading;

    const payerNotConnectedMessage = useMemo(
      () => (
        <>
          <div css={bannerTextStyles}>
            <header>
              <Heading level={3} style={{ fontSize: theme.fontSize(16) }}>
                {t('Access website eligibility report by adding the payer connection')}
              </Heading>
              <Text style={{ fontSize: theme.fontSize(14) }}>
                {t('Currently showing EDI response. Connect your payer credentials to view the full website report.')}
              </Text>
            </header>
            <aside css={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Button
                variant='tertiary'
                trackingId={insuranceReportTrackingIds.addPayerConnectionButton}
                onClick={onManagePayerConnection}
              >
                {t('Add Payer Credential')}
              </Button>
              <Button iconName='x-small' variant='secondary' onClick={handleCloseBanner} />
            </aside>
          </div>
        </>
      ),
      [t, handleCloseBanner, onManagePayerConnection]
    );
    return (
      <header css={reportHeaderStyles}>
        <div css={[headerContentStyles, !showBanner && shadowStyle]}>
          <div css={headerDetailsStyle}>
            <Heading
              level={1}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '650px', whiteSpace: 'nowrap' }}
            >
              {patientFullName}
            </Heading>
            {!isReportLoading && (
              <div css={tagsContainerStyle}>
                {shouldShowInsuranceStatus && (
                  <Chip.Tag
                    variant={isActiveInsurance ? 'success' : 'critical'}
                    css={chipStyles}
                    leftElement={
                      <Icon
                        name={isActiveInsurance ? 'shield-small' : 'insurance-uninsured-small'}
                        css={{ color: 'currentcolor' }}
                      />
                    }
                  >
                    {isActiveInsurance && (
                      <Text size='medium' style={{ color: theme.colors.success50 }}>
                        {t('Active')}
                      </Text>
                    )}
                    {isInActiveInsurance && (
                      <Text size='medium' style={{ color: theme.colors.critical50 }}>
                        {t('Inactive')}
                      </Text>
                    )}
                  </Chip.Tag>
                )}

                <Chip.Tag color='indigo' css={{ maxWidth: theme.spacing(20), backgroundColor: theme.colors.indigo10 }}>
                  <Text size='medium' css={{ color: theme.colors.indigo70 }}>
                    {insuranceTypeFormatted}
                  </Text>
                </Chip.Tag>

                {reportSources?.map((source) => {
                  const hasError = source?.status === 'error';
                  return (
                    <ResponseSourceTooltip
                      key={source.source}
                      title={formatSourceResponse(source.source)}
                      hoverText={removeUnderScore(source.error || '')}
                      placement='bottom-start'
                      hasError={hasError}
                    />
                  );
                })}
                <Text
                  size='small'
                  style={{ color: theme.colors.neutral50, whiteSpace: 'nowrap', alignSelf: 'flex-end' }}
                >
                  {t('Verified on ')}
                  <Text
                    size='small'
                    as='span'
                    style={{ fontWeight: theme.font.weight.bold, color: theme.colors.neutral50 }}
                  >
                    {reportDateFormat(verifiedDate)}
                  </Text>
                </Text>
              </div>
            )}
          </div>
          <div css={buttonContainerStyle}>
            <Button variant='primary' size='small' onClick={handlePrint} iconName='download-small' />
            {shouldShowSaveToPmsButton && (
              <Button
                variant='secondary'
                size='small'
                onClick={handleSaveToPms}
                trackingId='save-to-pms-button-tuuthfairy-report'
                css={{ width: 'fit-content' }}
              >
                {t('Save to {{PMSName}}', { PMSName })}
              </Button>
            )}
            <Button variant='secondary' size='small' onClick={closeInsuranceReportModal} iconName='x' />
          </div>
        </div>
        {showBanner && (
          <BannerNotification
            css={[shadowStyle, { borderRadius: 0, border: 0 }]}
            status='warn'
            fullWidth
            message={payerNotConnectedMessage}
          />
        )}
      </header>
    );
  }
);

InsuranceReportHeader.displayName = 'InsuranceReportHeader';

const headerContentStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(3, 2),
  height: theme.spacing(9),
  backgroundColor: theme.colors.white,
  zIndex: theme.zIndex.high,
});

const buttonContainerStyle = css`
  background-color: transparent;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing(1)};
  @media print {
    display: none;
  }
`;

const headerDetailsStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.spacing(1)};
  flex-wrap: wrap;
  margin-right: ${theme.spacing(1)};
  h1 {
    margin-right: ${theme.spacing(1)};
  }
`;

const tagsContainerStyle = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
});

const chipStyles = css({
  width: 'fit-content',
  maxWidth: theme.spacing(16),
});

const shadowStyle = css({ boxShadow: theme.shadows.light, width: '100%' });

const bannerTextStyles = css({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
});

const reportHeaderStyles = css({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  zIndex: theme.zIndex.high,
});
